import { gql, useQuery } from "@apollo/client";
import { Product3DAssetReference } from "../products/product3DTypes";

export const CATALOG_TEXTURE_LIMITS = {
  maxTextureSize: 1024,
  lowEndMaxTextureSize: 512,
  maxTextureBytes: 256 * 1024 * 1024,
  lowEndMaxTextureBytes: 128 * 1024 * 1024,
  maxAnisotropy: 4,
} as const;

export type CatalogImageOwner = {
  id: string;
  name?: string | null;
  description?: string | null;
  images?: string[] | null;
  additionalImages?: string[] | null;
  aiGeneratedImages?: Array<{
    angle?: string | null;
    imageUrl?: string | null;
    imageUrlNoBg?: string | null;
    sourceImageUrl?: string | null;
  } | null> | null;
  brand?: string | null;
  price?: number | null;
  url?: string | null;
  supplier?: {
    id?: string | null;
    name?: string | null;
    logo?: string | null;
  } | null;
  width?: number | null;
  height?: number | null;
  depth?: number | null;
  features3?: Record<string, unknown> | null;
  product3DAssets?: Product3DAssetReference[] | null;
  active3DAsset?: Product3DAssetReference | null;
  categories?: Array<{
    id?: string | null;
    name?: string | null;
    parent?: { id?: string | null; name?: string | null } | null;
  } | null> | null;
  categoriesMeta?: Array<{
    id?: string | null;
    name?: string | null;
    parent?: { id?: string | null; name?: string | null } | null;
  } | null> | null;
  colors?: Array<{
    id?: string | null;
    name?: string | null;
    hex?: string | null;
  } | null> | null;
};

export type CatalogListVariables = {
  limit: number;
  skip: number;
  search?: string;
  categoryIds?: string[];
  colorIds?: string[];
  materialIds?: string[];
  patternIds?: string[];
  styleIds?: string[];
  supplierIds?: string[];
  supplierNames?: string[];
  brands?: string[];
  priceRange?: { from?: number; to?: number };
  sortBy?: "priceLow" | "priceHigh" | "name" | "updatedAt";
  hasPublished3DAsset?: boolean;
  product3DLibraryOrigin?: "BRAND" | "BONI";
};

/** Shared with favorites query so library cards + insert get the same product shape. */
export const FLOORPLANNER_PRODUCT_3D_ASSET_FIELDS = gql`
  fragment FloorplannerProduct3DAssetFields on Product3DAsset {
    id
    productId
    variantId
    source
    status
    glbUrl
    thumbnailUrl
    footprintSvg
    footprintSource
    architectureSymbolKey
    dimensions
    modelDimensions
    dimensionSource
    dimensionAnchor
    dimensionScale
    bounds
    placement
    materialSlots
    quality
    review
  }
`;

/** Catalog product fields used by the product library and My Favorites. */
export const FLOORPLANNER_CATALOG_PRODUCT_FIELDS = gql`
  ${FLOORPLANNER_PRODUCT_3D_ASSET_FIELDS}
  fragment FloorplannerCatalogProductFields on Product {
    id
    name
    brand
    price
    description
    width
    height
    depth
    images
    additionalImages
    aiGeneratedImages {
      angle
      imageUrl
      imageUrlNoBg
      sourceImageUrl
    }
    url
    supplier {
      id
      name
      logo
    }
    categoriesMeta {
      id
      name
      parent {
        id
        name
      }
    }
    colors {
      id
      name
      hex
    }
    features3
    breakColors
    active3DAsset {
      ...FloorplannerProduct3DAssetFields
    }
  }
`;

export const FLOORPLANNER_PRODUCTS_META_QUERY = gql`
  ${FLOORPLANNER_CATALOG_PRODUCT_FIELDS}
  query FloorplannerProductsMeta(
    $limit: Int!
    $skip: Int!
    $search: String
    $categoryIds: [String!]
    $colorIds: [String!]
    $materialIds: [String!]
    $patternIds: [String!]
    $styleIds: [String!]
    $supplierIds: [ID]
    $supplierNames: [String!]
    $brands: [String]
    $priceRange: PriceRange
    $sortBy: SortOption
    $hasPublished3DAsset: Boolean
    $product3DLibraryOrigin: Product3DLibraryOrigin
  ) {
    productsMeta(
      input: {
        limit: $limit
        skip: $skip
        search: $search
        categoryIds: $categoryIds
        colorIds: $colorIds
        materialIds: $materialIds
        patternIds: $patternIds
        styleIds: $styleIds
        supplierIds: $supplierIds
        supplierNames: $supplierNames
        brands: $brands
        priceRange: $priceRange
        sortBy: $sortBy
        hasPublished3DAsset: $hasPublished3DAsset
        product3DLibraryOrigin: $product3DLibraryOrigin
      }
    ) {
      data {
        ...FloorplannerCatalogProductFields
      }
      total
    }
  }
`;

export const FLOORPLANNER_PRODUCT_3D_ASSETS_QUERY = gql`
  ${FLOORPLANNER_PRODUCT_3D_ASSET_FIELDS}
  query FloorplannerProduct3DAssets($productIds: [ID!]!, $status: String) {
    product3DAssets(productIds: $productIds, status: $status) {
      ...FloorplannerProduct3DAssetFields
    }
  }
`;

export const FLOORPLANNER_PRODUCT_LIBRARY_FILTERS_QUERY = gql`
  query FloorplannerProductLibraryFilters($origin: Product3DLibraryOrigin) {
    publishedProduct3DLibraryFilters(origin: $origin) {
      productCount
      priceMin
      priceMax
      categories {
        id
        name
        parent {
          id
          name
        }
        children {
          id
          name
          parent {
            id
            name
          }
          children {
            id
            name
            parent {
              id
              name
            }
          }
        }
      }
      brands {
        id
        name
      }
      suppliers {
        id
        name
        logo
      }
      colors {
        id
        name
        parent {
          id
          name
        }
      }
      shapes {
        id
        name
        parent {
          id
          name
        }
      }
      materials {
        id
        name
        parent {
          id
          name
        }
      }
      styles {
        id
        name
        parent {
          id
          name
        }
      }
      patterns {
        id
        name
        parent {
          id
          name
        }
      }
      rooms {
        id
        name
        parent {
          id
          name
        }
      }
    }
  }
`;

export const FLOORPLANNER_PRODUCT_BY_ID_QUERY = gql`
  ${FLOORPLANNER_CATALOG_PRODUCT_FIELDS}
  query FloorplannerProductById($id: String!) {
    productById(id: $id) {
      ...FloorplannerCatalogProductFields
    }
  }
`;

export const FLOORPLANNER_SURFACES_QUERY = gql`
  query FloorplannerSurfaces(
    $limit: Int!
    $skip: Int!
    $categoryIds: [String!]
    $colorIds: [String!]
  ) {
    surfaces(
      input: {
        limit: $limit
        skip: $skip
        categoryIds: $categoryIds
        colorIds: $colorIds
      }
    ) {
      data {
        id
        name
        description
        images
        categories {
          id
          name
          parent {
            name
          }
        }
        colors {
          id
          name
          hex
        }
        roomElements {
          height
          width
          segments {
            name
            type
          }
        }
      }
      total
    }
  }
`;

export const FLOORPLANNER_SURFACE_BY_ID_QUERY = gql`
  query FloorplannerSurfaceById($id: String!) {
    surfaceById(id: $id) {
      id
      name
      description
      images
      categories {
        id
        name
        parent {
          name
        }
      }
      colors {
        id
        name
        hex
      }
      roomElements {
        height
        width
        segments {
          name
          type
        }
      }
    }
  }
`;

export const FLOORPLANNER_SURFACE_CATEGORIES_QUERY = gql`
  query FloorplannerSurfaceCategories {
    surfaceCategories {
      id
      name
      order
      parent {
        id
        name
      }
      children {
        id
        name
      }
    }
  }
`;

export const FLOORPLANNER_MATERIALS_META_QUERY = gql`
  query FloorplannerMaterialsMeta {
    materialsMeta {
      id
      name
      description
      images
      categories {
        id
        name
        parent {
          name
        }
      }
      colors {
        id
        name
        hex
      }
    }
  }
`;

export function useFloorplannerSurfaces(
  variables: CatalogListVariables,
  skip = false,
) {
  return useQuery(FLOORPLANNER_SURFACES_QUERY, {
    variables,
    fetchPolicy: "cache-and-network",
    skip,
  });
}

export function useFloorplannerProducts(
  variables: CatalogListVariables,
  skip = false,
) {
  return useQuery(FLOORPLANNER_PRODUCTS_META_QUERY, {
    variables,
    fetchPolicy: "cache-and-network",
    // So `loading` is true on search/filter refetch, not only the first fetch.
    notifyOnNetworkStatusChange: true,
    skip,
  });
}

export function useFloorplannerProductById(id?: string, skip = false) {
  return useQuery(FLOORPLANNER_PRODUCT_BY_ID_QUERY, {
    variables: { id },
    fetchPolicy: "cache-and-network",
    skip: skip || !id,
  });
}

export function useFloorplannerSurfaceById(id?: string, skip = false) {
  return useQuery(FLOORPLANNER_SURFACE_BY_ID_QUERY, {
    variables: { id },
    fetchPolicy: "cache-and-network",
    skip: skip || !id,
  });
}

export function useFloorplannerSurfaceCategories(skip = false) {
  return useQuery(FLOORPLANNER_SURFACE_CATEGORIES_QUERY, {
    fetchPolicy: "cache-and-network",
    skip,
  });
}

/** Full CategoryMeta tree — used to resolve Wallpaper category ids by name. */
export const FLOORPLANNER_CATEGORIES_META_QUERY = gql`
  query FloorplannerCategoriesMeta {
    categoriesMeta {
      id
      name
      label
      children {
        id
        name
        label
        children {
          id
          name
          label
          children {
            id
            name
            label
          }
        }
      }
    }
  }
`;

export function useFloorplannerCategoriesMeta(skip = false) {
  return useQuery(FLOORPLANNER_CATEGORIES_META_QUERY, {
    fetchPolicy: "cache-first",
    skip,
  });
}

/**
 * Brands available for products in the given categories (Wallpaper / Flooring).
 * Used only by the surface-product browse panel — not the 3D Product Library.
 */
export const FLOORPLANNER_PRODUCT_BRANDS_QUERY = gql`
  query FloorplannerProductBrands($categoriesIds: [String]!) {
    productBrands(categoriesIds: $categoriesIds) {
      id
      name
    }
  }
`;

export function useFloorplannerProductBrands(
  categoriesIds: string[],
  skip = false,
) {
  return useQuery(FLOORPLANNER_PRODUCT_BRANDS_QUERY, {
    variables: { categoriesIds },
    fetchPolicy: "cache-first",
    skip: skip || categoriesIds.length === 0,
  });
}

export function useFloorplannerMaterials(skip = false) {
  return useQuery(FLOORPLANNER_MATERIALS_META_QUERY, {
    fetchPolicy: "cache-and-network",
    skip,
  });
}

export function useFloorplannerProduct3DAssets(
  productIds: string[],
  skip = false,
) {
  return useQuery(FLOORPLANNER_PRODUCT_3D_ASSETS_QUERY, {
    variables: { productIds, status: "ready" },
    fetchPolicy: "cache-and-network",
    skip: skip || productIds.length === 0,
  });
}

export function useFloorplannerProductLibraryFilters(
  origin?: "BRAND" | "BONI",
  skip = false,
) {
  return useQuery(FLOORPLANNER_PRODUCT_LIBRARY_FILTERS_QUERY, {
    variables: { origin },
    fetchPolicy: "cache-first",
    skip,
  });
}
